import { useEffect, useState } from 'react';
import { Button, Flex, Form, Input, message, Table } from 'antd';
import CommonModalFactory from '@/plugins/ModalFactory';
import { ColumnsType } from 'antd/es/table';
import { openDeleteModal } from '@/components/Modal/common';
import { useRequest, useSetState } from 'ahooks';
import { ECompactWordType, phraseApi, PhraseItemRes } from '@/axios/company';
import { CompactWordCollapse } from '../CompactWord';
import AccessButton from '@/components/AccessButton';
import OperatorBtns from '@/components/Table/operateBtns';
import EllipsisText from '@/components/Table/EllipsisText';
import CommonTable from '@/components/Table';

const ExpandWordTable = ({
  compactWords,
  companyId,
}: {
  compactWords: { [key in ECompactWordType]: string[] };
  companyId: string;
}) => {
  // 搜索词组列表
  const [phraseParams, setPhraseParams] = useSetState<Parameters<typeof phraseApi.list>[0]>({
    pageIndex: 1,
    pageSize: 10,
    companyId,
    type: 'EXPAND_WORD',
  });
  const {
    data: phraseList,
    runAsync: refreshPhraseList,
    loading,
  } = useRequest(
    () => {
      return phraseApi.list(phraseParams);
    },
    {
      refreshDeps: [phraseParams],
    }
  );
  const columns: ColumnsType<PhraseItemRes> = [
    {
      title: '扩展规则名称',
      dataIndex: 'name',
      width: 200,
    },
    {
      title: '使用词类型',
      dataIndex: 'categories',
      render: (value, record) => {
        const text = record.categories
          ?.filter((row) => {
            return row.words && row.words.length > 0;
          })
          .map((item) => item.description + (item.words?.length ? `(${item.words?.join('、')})` : ''))
          .join('\n');
        return (
          <EllipsisText
            text={text}
            tooltip={
              <div
                style={{
                  whiteSpace: 'pre-wrap',
                }}
              >
                {text}
              </div>
            }
          />
        );
      },
    },
    {
      title: '操作',
      width: 150,
      render: (_, record) => {
        return (
          <OperatorBtns
            btns={[
              {
                type: 'edit',
                permission: ['company:search_word:component_word_record:edit'],
                onClick: () => {
                  openExtendRuleModal(
                    {
                      recordId: record.recordId,
                      name: record.name,
                      compactWords,
                      categories: record.categories,
                    },
                    {
                      title: '编辑扩展规则',
                      okText: '保存',
                    },
                    () => {
                      message.success('更新成功');
                      refreshPhraseList();
                    }
                  );
                },
              },
              {
                type: 'delete',
                onClick: () => {
                  openDeleteModal(
                    {
                      content: '确认删除该扩展规则吗？',
                      api: async () => {
                        // 删除接口调用
                        phraseApi.delete([record.recordId]);
                      },
                    },
                    undefined,
                    refreshPhraseList
                  );
                },
                permission: ['company:search_word:component_word_record:delete'],
              },
            ]}
          />
        );
      },
    },
  ];
  const [selectedRows, setSelectedRows] = useState<React.Key[]>([]);
  return (
    <div>
      <Flex gap={8} align="center" style={{ marginBottom: '20px' }}>
        {selectedRows.length === 0 && (
          <AccessButton permission={['company:search_word:component_word_record:create']}>
            <Button
              type="primary"
              onClick={() => {
                openExtendRuleModal(
                  {
                    compactWords,
                    companyId,
                  },
                  undefined,
                  (res) => {
                    message.success('创建成功');
                    setPhraseParams({ pageIndex: 1 });
                  }
                );
              }}
            >
              创建扩展规则
            </Button>
          </AccessButton>
        )}
        {selectedRows.length > 0 && (
          <>
            <Button
              onClick={async () => {
                setSelectedRows([]);
              }}
            >
              取消选择({selectedRows.length})
            </Button>
            <AccessButton permission={['company:search_word:component_word_record:delete']}>
              <Button
                type="primary"
                danger
                onClick={() => {
                  openDeleteModal(
                    {
                      content: `确定删除选中的${selectedRows.length}个扩展规则吗？`,
                      api: () => phraseApi.delete(selectedRows as string[]),
                    },
                    undefined,
                    () => {
                      setPhraseParams({ pageIndex: 1 });
                      setSelectedRows([]);
                    }
                  );
                }}
              >
                批量删除({selectedRows.length})
              </Button>
            </AccessButton>
          </>
        )}
      </Flex>
      <CommonTable<PhraseItemRes>
        loading={loading}
        rowKey={'recordId'}
        columns={columns}
        dataSource={phraseList?.records || []}
        total={phraseList?.total}
        params={phraseParams}
        setParams={setPhraseParams}
        rowSelection={{
          preserveSelectedRowKeys: true,
          selectedRowKeys: selectedRows,
          onChange: (selectedRowKeys) => {
            setSelectedRows(selectedRowKeys);
          },
        }}
        scroll={{ x: '770px' }}
      />
    </div>
  );
};
export default ExpandWordTable;

// 选择扩展规则
export const openExtendRuleModal = CommonModalFactory<{
  compactWords: { [key in ECompactWordType]: string[] };
  companyId: string;
  categories?: {
    type: ECompactWordType;
    description: string;
    words: string[];
  }[];
  recordId?: string;
  name?: string;
}>(
  {
    title: '创建扩展规则',
    width: 800,
    styles: {
      body: {
        maxHeight: '70vh',
        overflowY: 'auto',
        padding: '16px',
      },
    },
  },
  ({ data, setOkFn }) => {
    const [searchPhrase, setSearchPhrase] = useState<{
      [key in ECompactWordType]: string[];
    }>({
      [ECompactWordType.QuestionPrefix]: [],
      [ECompactWordType.LocationPrefix]: [],
      [ECompactWordType.RecommendPrefix]: [],
      [ECompactWordType.BrandPrefix]: [],
      [ECompactWordType.ScenePrefix]: [],
      [ECompactWordType.Core]: [],
      [ECompactWordType.EntitySuffix]: [],
      [ECompactWordType.RecommendSuffix]: [],
      [ECompactWordType.QuestionAnswerSuffix]: [],
    });
    const [form] = Form.useForm();
    useEffect(() => {
      console.log(data?.categories);
      if (data?.recordId) {
        form.setFieldsValue({
          name: data.name,
        });
        if (data?.categories) {
          const newSearchPhrase = { ...searchPhrase };
          data.categories.forEach((item) => {
            newSearchPhrase[item.type] = item.words;
          });
          setSearchPhrase({
            ...searchPhrase,
            ...newSearchPhrase,
          });
        }
      }
    }, [data]);
    useEffect(() => {
      setOkFn(async () => {
        const values = await form.validateFields();
        // exclude core
        const { [ECompactWordType.Core]: _, ...rest } = searchPhrase;
        const wordMap = Object.entries(rest).map(([key, words]) => ({
          type: key as ECompactWordType,
          words,
        }));
        if (data?.recordId) {
          await phraseApi.updateExpand({
            recordId: data.recordId,
            name: values.name,
            wordMap,
          });
        } else {
          await phraseApi.createExpand({
            name: values.name,
            companyId: data.companyId,
            wordMap,
          });
        }
      });
    }, [searchPhrase, form, data]);
    return (
      <Form form={form} initialValues={{}}>
        <Form.Item name="name" label="规则名称" rules={[{ required: true, message: '请输入规则名称' }]}>
          <Input placeholder="请输入规则名称" />
        </Form.Item>
        <Flex vertical gap={8} style={{ flex: 1, background: '#F7F8FA', padding: '12px' }}>
          <CompactWordCollapse
            excludeType={ECompactWordType.Core}
            compactWords={data?.compactWords}
            searchPhrase={searchPhrase}
            setSearchPhrase={setSearchPhrase}
          />
        </Flex>
      </Form>
    );
  }
);
